import { Menu } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { FaRegUserCircle } from "react-icons/fa";
import { IoLanguage } from "react-icons/io5";
import { getLoginUrl } from "../utils/auth";
import { getAccessToken, removeTokens } from "../utils/token";
import { getWhiteLabel } from "../utils/white-label";
import { Container } from "./container";
import { Link } from "./link";
import { ListBox } from "./list-box";
import { Loading } from "./loading";
import type { TransNamespace } from "../types";
import type { ReactNode } from "react";

export type HeaderProps = {
  logo?: {
    src: string;
    alt: string;
    href: string;
  };
  languageMenuProps?: {
    languages: {
      locale: string;
      name: string;
      englishName: string;
      href: string;
    }[];
    locale: string;
  };
  currencyMenuProps?: {
    currencies: {
      code: string;
      name: string;
      href: string;
      symbol: string;
    }[];
    loading?: boolean;
    code: string;
    onOpen?: () => void;
  };
};

export const Header = (props: HeaderProps) => {
  const {
    logo = {
      src: "https://static.mozio.com/assets/logo.svg",
      alt: "Mozio logo",
      href: "/",
    },
    languageMenuProps,
    currencyMenuProps,
  } = props;

  const { t } = useTranslation<TransNamespace>();
  const { header } = getWhiteLabel();

  const selectedLanguage =
    (languageMenuProps &&
      languageMenuProps.languages.find(
        (l) => l.locale === languageMenuProps.locale,
      )) ||
    languageMenuProps?.languages[0];

  return (
    <header className="fixed left-0 right-0 top-0 z-30 h-[var(--mz-header-height)] bg-white shadow-md">
      <Container className="flex h-full max-w-none justify-between">
        <Link className="flex" color="inherit" href={logo.href}>
          <img
            className="h-[var(--mz-header-logo-height)] self-center"
            src={logo.src}
            alt={logo.alt}
          />
        </Link>

        <div className="flex">
          {languageMenuProps && (
            <HeaderMenu
              buttonProps={{
                content: selectedLanguage!.name,
                icon: <IoLanguage />,
              }}
              options={languageMenuProps.languages.map((language) => ({
                value: language.locale,
                content:
                  selectedLanguage?.locale === "en_US" &&
                  language.locale !== "en_US"
                    ? `${language.name} (${language.englishName})`
                    : language.name,
                href: language.href,
              }))}
            />
          )}

          {currencyMenuProps && (
            <HeaderMenu
              buttonProps={{
                content: currencyMenuProps.code,
              }}
              options={
                currencyMenuProps?.currencies.map((c) => ({
                  value: c.code,
                  content: (
                    <div className="grid grid-cols-[32px_1fr] gap-2">
                      <div>{c.code}</div>
                      <div>{c.name}</div>
                    </div>
                  ),
                  href: c.href,
                })) || []
              }
              loading={currencyMenuProps.loading}
              onOpen={currencyMenuProps.onOpen}
            />
          )}

          {header.showSignInButton && !getAccessToken() && (
            <a
              className="text-primary inline-flex h-full items-center px-3 text-sm duration-200 hover:bg-gray-200"
              href={getLoginUrl()}
            >
              {t("Sign in")}
            </a>
          )}

          {getAccessToken() && (
            <HeaderMenu
              buttonProps={{ icon: <FaRegUserCircle /> }}
              options={[
                {
                  value: "logout",
                  content: t("Log out"),
                  href: window.location.href,
                },
              ]}
              onOptionClick={(value) => value === "logout" && removeTokens()}
            />
          )}
        </div>
      </Container>
    </header>
  );
};

type HeaderMenuProps = {
  buttonProps: {
    content?: string;
    icon?: ReactNode;
  };
  options: { value: string; content: ReactNode; href: string }[];
  loading?: boolean;
  onOpen?: () => void;
  onOptionClick?: (value: string) => void;
};

const HeaderMenu = (props: HeaderMenuProps) => {
  const {
    buttonProps,
    options,
    loading = false,
    onOpen = () => {},
    onOptionClick = () => {},
  } = props;

  return (
    <Menu>
      {({ open }) => {
        return (
          <div className="relative h-full">
            <Menu.Button
              onClick={() => !open && onOpen()}
              className="text-primary relative inline-flex h-full items-center gap-2 px-3 text-sm duration-200 hover:bg-gray-200"
            >
              {buttonProps.icon && (
                <span className="pointer-events-none flex items-center [&>svg]:h-6 [&>svg]:w-6">
                  {buttonProps.icon}
                </span>
              )}
              {buttonProps.content && <span>{buttonProps.content}</span>}
            </Menu.Button>

            <ListBox.Transition>
              <div className="absolute left-1/2 z-10 mt-2 w-60 -translate-x-1/2 rounded bg-white shadow-lg">
                <div className="absolute -top-1 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1 transform">
                  <svg className="fill-current text-white" viewBox="0 0 12 12">
                    <path d="M6 0l6 6H0z" />
                  </svg>
                </div>

                <Menu.Items
                  as={ListBox}
                  className="relative mt-0 max-h-60 overflow-y-auto text-sm shadow-none"
                >
                  {loading && <Loading className="py-2" color="primary" />}
                  {options.map((option) => (
                    <Menu.Item
                      as={ListBox.Option}
                      key={option.value}
                      className="px-0 py-0"
                      onClick={() => onOptionClick(option.value)}
                    >
                      <a className="block px-3 py-2" href={option.href}>
                        {option.content}
                      </a>
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </div>
            </ListBox.Transition>
          </div>
        );
      }}
    </Menu>
  );
};
